import { useCallback, useEffect, useState } from "react";
import { isNative } from "@/lib/native";

const DISMISS_KEY = "douceur_install_dismissed";

export const isStandalone = () =>
  isNative || window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const isIOS = () => {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
};

// Captured globally so the prompt is not lost if it fires before a component mounts.
let deferredPrompt = null;
const listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    listeners.forEach((fn) => fn());
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    listeners.forEach((fn) => fn());
  });
}

export default function usePwaInstall() {
  const [, force] = useState(0);
  const [installed, setInstalled] = useState(isStandalone());
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

  useEffect(() => {
    const update = () => { force((n) => n + 1); setInstalled(isStandalone()); };
    listeners.add(update);
    return () => listeners.delete(update);
  }, []);

  // "native": browser install prompt available (Android/Chrome/Edge); "ios": manual Safari steps; "manual": other browsers
  let mode = "manual";
  if (deferredPrompt) mode = "native";
  else if (isIOS()) mode = "ios";

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    force((n) => n + 1);
    return outcome === "accepted";
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  }, []);

  return { installed, mode, dismissed, promptInstall, dismiss };
}
